import React, { useEffect, useState } from 'react';
import type { AdAccount, Page, Post, WizardTarget } from './useLaunchWizard.ts';

function BulkNamesModal({ selectedIds, onAdd, onClose }: { selectedIds: string[]; onAdd: (names: string[]) => void; onClose: () => void }) {
  const [text, setText] = useState('');

  const handleAdd = () => {
    const names = text.split('\n').map((l) => l.trim());
    onAdd(names);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Bulk custom names</h3>
          <button className="btn btn--ghost btn--sm" onClick={onClose}>✕</button>
        </div>
        <p className="text-muted text-sm">
          Paste campaign names — one per line. They will fill the <strong>Custom name</strong> column in order ({selectedIds.length} accounts selected).
        </p>
        <textarea
          className="input modal-textarea"
          placeholder={'Campaign_Name_1\nCampaign_Name_2\n...'}
          value={text}
          onChange={(e) => setText(e.target.value)}
          autoFocus
        />
        <div className="modal-actions">
          <button className="btn btn--secondary btn--sm" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary btn--sm" onClick={handleAdd}>Add</button>
        </div>
      </div>
    </div>
  );
}

export function AdsStep({
  targets,
  accounts,
  pages,
  posts,
  loadPosts,
  updateTarget,
  adsetsCount,
  setAdsetsCount,
  adsPerAdset,
  setAdsPerAdset,
  selectedOrder,
  scheduledAt,
}: {
  targets: Record<string, WizardTarget>;
  accounts: AdAccount[];
  pages: Page[];
  posts: Record<string, Post[]>;
  loadPosts: (page: string) => void;
  updateTarget: (id: string, patch: Partial<WizardTarget>) => void;
  adsetsCount: number;
  setAdsetsCount: (n: number) => void;
  adsPerAdset: number;
  setAdsPerAdset: (n: number) => void;
  selectedOrder: string[];
  scheduledAt: string | null;
}) {
  const [showBulkNames, setShowBulkNames] = useState(false);

  // Render accounts in selection order
  const selected = selectedOrder.map((id) => targets[id]).filter(Boolean);

  useEffect(() => {
    selected.forEach((target) => target.page_id && loadPosts(target.page_id));
  }, [selected.map((x) => x.page_id).join(',')]);

  // Convert ISO string to datetime-local value (strip seconds and timezone offset for input)
  const toDatetimeLocal = (iso: string | null): string => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      // Format as YYYY-MM-DDTHH:MM in local +3 timezone
      const offset = 3 * 60; // +3
      const local = new Date(d.getTime() + offset * 60000);
      return local.toISOString().slice(0, 16);
    } catch {
      return '';
    }
  };

  const handleBulkNames = (names: string[]) => {
    selected.forEach((target, i) => {
      const name = names[i]?.trim() ?? '';
      updateTarget(target.ad_account_id, { custom_name: name });
    });
  };

  return (
    <div className="wizard-step">
      {showBulkNames && (
        <BulkNamesModal
          selectedIds={selected.map((t) => t.ad_account_id)}
          onAdd={handleBulkNames}
          onClose={() => setShowBulkNames(false)}
        />
      )}

      <div className="inline-fields">
        <label className="field">
          <span className="label">Ad sets</span>
          <input className="input" min="1" type="number" value={adsetsCount} onChange={(e) => setAdsetsCount(Math.max(1, Number(e.target.value)))} />
        </label>
        <label className="field">
          <span className="label">Ads per ad set</span>
          <input className="input" min="1" type="number" value={adsPerAdset} onChange={(e) => setAdsPerAdset(Math.max(1, Number(e.target.value)))} />
        </label>
        <button className="btn btn--outline btn--sm" style={{ alignSelf: 'flex-end' }} onClick={() => setShowBulkNames(true)}>⚡ Bulk Names</button>
      </div>
      <p className="text-muted text-sm">N-M-K: 1 campaign, {adsetsCount} ad sets, {adsPerAdset} ads in each.</p>

      <div className="wizard-table">
        <div className="wizard-row wizard-row--ads wizard-row--header">
          <span>Ad account</span>
          <span>Fan Page</span>
          <span>Custom name</span>
          <span>Start time (+3)</span>
          <span>Post</span>
        </div>

        {selected.map((target) => (
          <div className="wizard-row wizard-row--ads" key={target.ad_account_id}>
            <div>
              <strong>{accounts.find((a) => a.id === target.ad_account_id)?.name ?? target.ad_account_id}</strong>
              <small>{target.ad_account_id}</small>
            </div>
            <div>{pages.find((p) => p.id === target.page_id)?.name ?? target.page_id}</div>
            <input
              className="input"
              placeholder="Custom name (optional)"
              value={target.custom_name}
              onChange={(e) => updateTarget(target.ad_account_id, { custom_name: e.target.value })}
              title="Overrides template macro patterns for campaign, adset, and ad names for this account"
            />
            <input
              className="input"
              type="datetime-local"
              value={toDatetimeLocal(target.scheduled_at ?? scheduledAt)}
              onChange={(e) => {
                const val = e.target.value;
                updateTarget(target.ad_account_id, {
                  scheduled_at: val ? new Date(`${val}+03:00`).toISOString() : null,
                });
              }}
              title="Start time for this account's campaign (overrides global setting)"
            />
            <select className="input" value={target.post_id} onChange={(e) => updateTarget(target.ad_account_id, { post_id: e.target.value })}>
              <option value="">Select post</option>
              {(posts[target.page_id] ?? []).map((post) => (
                <option key={post.id} value={post.id} title={post.message ?? ''}>
                  {(post.message ?? 'Untitled post').slice(0, 30)}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}
